import { normalizeMarketplace as normalizeMarketplaceCode } from '../../shared/marketplaces'
import type { FunnelSummary } from './managedApi'
import type { FunnelSellerOption } from './useContentFunnelOptions'

const CYRILLIC_TO_LATIN: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'i',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'c',
  ч: 'ch',
  ш: 'sh',
  щ: 'sch',
  ы: 'y',
  э: 'e',
  ю: 'yu',
  я: 'ya',
}

/**
 * Площадка в написании правил воронок: `wb`, `ozon`, `ym`, `site`.
 * Известные площадки — через реестр (`wildberries` → `wb`,
 * `yandex_market` → `ym`); каналы вне реестра (`site`) проходят как есть.
 */
export function normalizeMarketplace(value: unknown) {
  const normalized = String(value || '').trim().toLowerCase()
  const code = normalizeMarketplaceCode(normalized)
  if (code) return code
  if (normalized.includes('wildberries')) return 'wb'
  if (normalized.includes('ozon') || normalized.includes('озон')) return 'ozon'
  return normalized
}

/**
 * Legacy PHOTOGEN stores cabinet initials such as `ksa`, while the sellers
 * directory contains user-facing short names such as `ВБ КСА`.
 */
export function cabinetAlias(value: unknown) {
  const withoutMarketplace = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\b(?:wildberries|wb|ozon|ym|yandex_market)\b/gi, ' ')
    .replace(/(?:вб|озон)/gi, ' ')
    .replace(/(?:^|\s)ям(?=\s|$)/gi, ' ')
  return Array.from(withoutMarketplace)
    .map((character) => CYRILLIC_TO_LATIN[character] ?? character)
    .join('')
    .replace(/[^a-z0-9]/g, '')
}

export function sellerMatchesFunnel(
  seller: FunnelSellerOption,
  funnel: Pick<FunnelSummary, 'seller_id' | 'cabinet' | 'marketplace'>,
) {
  const funnelMarketplace = normalizeMarketplace(funnel.marketplace)
  const sellerMarketplace = normalizeMarketplace(seller.marketplace)
  if (
    funnelMarketplace &&
    funnelMarketplace !== 'any' &&
    sellerMarketplace &&
    funnelMarketplace !== sellerMarketplace
  ) {
    return false
  }
  if (funnel.seller_id && String(funnel.seller_id) === seller.id) return true
  if (String(funnel.cabinet || '') === seller.id) return true
  // Правило хранит машинный код кабинета (`ksa_ym`) — он и есть идентичность.
  const funnelCabinet = String(funnel.cabinet || '').trim().toLowerCase()
  if (seller.cabinetCode && funnelCabinet === seller.cabinetCode.trim().toLowerCase()) return true
  const funnelAlias = cabinetAlias(funnel.cabinet)
  return Boolean(funnelAlias && funnelAlias === cabinetAlias(seller.cabinet))
}

export function resolveFunnelSeller(
  sellers: FunnelSellerOption[],
  funnel: Pick<FunnelSummary, 'seller_id' | 'cabinet' | 'marketplace'>,
) {
  return sellers.find((seller) => sellerMatchesFunnel(seller, funnel))
}

/**
 * Значения фильтра «Тематика» берутся только из тематического атрибута
 * задания. `name` намеренно не используется: это наименование задания.
 */
export function themeAttributeOptions(
  items: Array<{ theme?: string | null }>,
): string[] {
  return Array.from(
    new Set(
      items
        .map((item) => item.theme?.trim())
        .filter((value): value is string => Boolean(value)),
    ),
  )
}

function normalizedType(value: unknown) {
  return String(value || '')
    .trim()
    .toLocaleLowerCase('ru')
    .replace(/ё/g, 'е')
}

function compactType(value: unknown) {
  return normalizedType(value).replace(/[^a-zа-я0-9]/g, '')
}

function findDictionaryValue(dictionary: string[], ...candidates: string[]) {
  const candidateSet = new Set(candidates.map(normalizedType))
  return dictionary.find((value) => candidateSet.has(normalizedType(value)))
}

export function canonicalDesignType(value: unknown, dictionary: string[]) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const direct = dictionary.find((item) => normalizedType(item) === normalizedType(raw))
  if (direct) return direct

  const compact = compactType(raw)
  const cupcakeSize = compact.match(/(?:cup|капк)([568])/i)?.[1]
  if (cupcakeSize) {
    return findDictionaryValue(dictionary, `Капк ${cupcakeSize}`) || raw
  }
  if (compact.includes('круг') || compact === 'krug' || compact === 'round') {
    return findDictionaryValue(dictionary, 'Круг') || raw
  }
  if (compact.includes('топпер') || compact === 'topper') {
    return findDictionaryValue(dictionary, 'Топпер') || raw
  }
  if (compact.includes('эклер') || compact === 'ekler') {
    return findDictionaryValue(dictionary, 'Эклеры') || raw
  }
  if (compact.includes('квадрат') || compact === 'kvadrat' || compact === 'square') {
    return findDictionaryValue(dictionary, 'Квадрат') || raw
  }
  if (compact.includes('паттерн') || compact === 'patterns') {
    return findDictionaryValue(dictionary, 'Паттерны') || raw
  }
  return raw
}
